import { call, put, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { erpApi } from '../api/erp-api';
import {
    // Receivables
    getReceivablesRequest,
    getReceivablesSuccess,
    getReceivablesStatsRequest,
    getReceivablesStatsSuccess,
    receivablesFailure,
    createReceivableRequest,
    createReceivableSuccess,

    // Cash Flow
    getCashFlowRequest,
    getCashFlowSuccess,
    getCashFlowStatsRequest,
    getCashFlowStatsSuccess,
    createCashFlowTransactionRequest,
    createCashFlowTransactionSuccess,
    updateCashFlowTransactionRequest,
    updateCashFlowTransactionSuccess,
    deleteCashFlowTransactionRequest,
    deleteCashFlowTransactionSuccess,
    cashFlowFailure,

    // Commissions
    getCommissionsRequest,
    getCommissionsSuccess,
    getCommissionsStatsRequest,
    getCommissionsStatsSuccess,
    updateCommissionRequest,
    updateCommissionSuccess,
    updateCommissionFailure,
    commissionsFailure,

    // Income Form
    submitIncomeFormRequest,
    submitIncomeFormSuccess,
    submitIncomeFormFailure,
    clearIncomeForm,

    // Request types
    GetReceivablesRequest,
    CreateReceivableRequest,
    GetCashFlowRequest,
    GetCommissionsRequest,
    CreateCashFlowTransactionRequest,
    UpdateCashFlowTransactionRequest,
    DeleteCashFlowTransactionRequest,
} from './erp-slice';

// ============================================================================
// RECEIVABLES SAGAS
// ============================================================================

function* getReceivablesSaga(
    action: PayloadAction<GetReceivablesRequest>
): Generator<any, void, any> {
    try {
        const response: any = yield call(erpApi.receivables.getAll, action.payload);

        yield put(getReceivablesSuccess({
            data: response.data,
            total: response.total,
            page: response.page,
            limit: response.limit,
            totalPages: response.totalPages,
        }));
    } catch (error: any) {
        yield put(
            receivablesFailure(
                error.response?.data?.message || 'Error al obtener las cuentas por cobrar'
            )
        );
    }
}

function* getReceivablesStatsSaga(): Generator<any, void, any> {
    try {
        const stats: any = yield call(erpApi.receivables.getStats);
        yield put(getReceivablesStatsSuccess(stats));
    } catch (error: any) {
        yield put(
            receivablesFailure(
                error.response?.data?.message || 'Error al obtener estadísticas de cuentas por cobrar'
            )
        );
    }
}

function* createReceivableSaga(
    action: PayloadAction<CreateReceivableRequest>
): Generator<any, void, any> {
    try {
        const newInvoice: any = yield call(erpApi.receivables.create, action.payload);
        yield put(createReceivableSuccess(newInvoice));

        // Refrescar estadísticas después de crear
        yield put(getReceivablesStatsRequest());
    } catch (error: any) {
        yield put(
            receivablesFailure(
                error.response?.data?.message || 'Error al crear la cuenta por cobrar'
            )
        );
    }
}

// ============================================================================
// CASH FLOW SAGAS
// ============================================================================

function* getCashFlowSaga(
    action: PayloadAction<GetCashFlowRequest>
): Generator<any, void, any> {
    try {
        const response: any = yield call(erpApi.cashFlow.getAll, action.payload);

        yield put(getCashFlowSuccess({
            data: response.data,
            total: response.total,
            page: response.page,
            limit: response.limit,
            totalPages: response.totalPages,
        }));
    } catch (error: any) {
        yield put(
            cashFlowFailure(
                error.response?.data?.message || 'Error al obtener transacciones de flujo de caja'
            )
        );
    }
}

function* getCashFlowStatsSaga(
    action: PayloadAction<{ startDate?: string; endDate?: string }>
): Generator<any, void, any> {
    try {
        const stats: any = yield call(
            erpApi.cashFlow.getStats,
            action.payload.startDate,
            action.payload.endDate
        );
        yield put(getCashFlowStatsSuccess(stats));
    } catch (error: any) {
        yield put(
            cashFlowFailure(
                error.response?.data?.message || 'Error al obtener estadísticas de flujo de caja'
            )
        );
    }
}

function* createCashFlowTransactionSaga(
    action: PayloadAction<CreateCashFlowTransactionRequest>
): Generator<any, void, any> {
    try {
        const newTransaction: any = yield call(erpApi.cashFlow.create, action.payload);
        yield put(createCashFlowTransactionSuccess(newTransaction));

        // Refrescar estadísticas después de crear
        yield put(getCashFlowStatsRequest({}));
    } catch (error: any) {
        yield put(
            cashFlowFailure(
                error.response?.data?.message || 'Error al crear transacción'
            )
        );
    }
}

function* updateCashFlowTransactionSaga(
    action: PayloadAction<UpdateCashFlowTransactionRequest>
): Generator<any, void, any> {
    try {
        const updatedTransaction: any = yield call(
            erpApi.cashFlow.update,
            action.payload.id,
            action.payload.data
        );
        yield put(updateCashFlowTransactionSuccess(updatedTransaction));

        // Refrescar estadísticas después de actualizar
        yield put(getCashFlowStatsRequest({}));
    } catch (error: any) {
        yield put(
            cashFlowFailure(
                error.response?.data?.message || 'Error al actualizar transacción'
            )
        );
    }
}

function* deleteCashFlowTransactionSaga(
    action: PayloadAction<DeleteCashFlowTransactionRequest>
): Generator<any, void, any> {
    try {
        yield call(erpApi.cashFlow.delete, action.payload.id);
        yield put(deleteCashFlowTransactionSuccess(action.payload.id));

        // Refrescar estadísticas después de eliminar
        yield put(getCashFlowStatsRequest({}));
    } catch (error: any) {
        yield put(
            cashFlowFailure(
                error.response?.data?.message || 'Error al eliminar transacción'
            )
        );
    }
}

// ============================================================================
// COMMISSIONS SAGAS
// ============================================================================

function* getCommissionsSaga(
    action: PayloadAction<GetCommissionsRequest>
): Generator<any, void, any> {
    try {
        const response: any = yield call(erpApi.commissions.getAll, action.payload);

        yield put(getCommissionsSuccess({
            data: response.data,
            total: response.total,
            page: response.page,
            limit: response.limit,
            totalPages: response.totalPages,
        }));
    } catch (error: any) {
        yield put(
            commissionsFailure(
                error.response?.data?.message || 'Error al obtener comisiones'
            )
        );
    }
}

function* getCommissionsStatsSaga(): Generator<any, void, any> {
    try {
        const stats: any = yield call(erpApi.commissions.getStats);
        yield put(getCommissionsStatsSuccess(stats));
    } catch (error: any) {
        yield put(
            commissionsFailure(
                error.response?.data?.message || 'Error al obtener estadísticas de comisiones'
            )
        );
    }
}

function* updateCommissionSaga(
    action: PayloadAction<{ id: string; data: any }>
): Generator<any, void, any> {
    try {
        const { id, data } = action.payload;
        // erpApi.commissions API surface is in flux; cast to any for now.
        // TODO: align with backend once /commissions/:id PATCH endpoint lands.
        const updatedCommission: any = yield call((erpApi.commissions as any).update, id, data);
        yield put(updateCommissionSuccess(updatedCommission));

        // Refrescar estadísticas después de actualizar
        yield put(getCommissionsStatsRequest());
    } catch (error: any) {
        yield put(
            updateCommissionFailure(
                error.response?.data?.message || 'Error al actualizar comisión'
            )
        );
    }
}

// ============================================================================
// INCOME FORM SAGAS
// ============================================================================

function* submitIncomeFormSaga(
    action: PayloadAction<CreateReceivableRequest>
): Generator<any, void, any> {
    try {
        // Crear la factura usando la API de receivables
        const newInvoice: any = yield call(erpApi.receivables.create, action.payload);

        // Dispatch success
        yield put(submitIncomeFormSuccess(newInvoice));

        // Refrescar estadísticas de receivables
        yield put(getReceivablesStatsRequest());

        // Limpiar el formulario
        yield put(clearIncomeForm());

        // Nota: El componente debería manejar la notificación de éxito
    } catch (error: any) {
        yield put(
            submitIncomeFormFailure(
                error.response?.data?.message || 'Error al registrar el ingreso'
            )
        );
    }
}

// ============================================================================
// WATCHERS
// ============================================================================

function* watchGetReceivables() {
    yield takeLatest(getReceivablesRequest.type, getReceivablesSaga);
}

function* watchGetReceivablesStats() {
    yield takeLatest(getReceivablesStatsRequest.type, getReceivablesStatsSaga);
}

function* watchCreateReceivable() {
    yield takeLatest(createReceivableRequest.type, createReceivableSaga);
}

function* watchGetCashFlow() {
    yield takeLatest(getCashFlowRequest.type, getCashFlowSaga);
}

function* watchGetCashFlowStats() {
    yield takeLatest(getCashFlowStatsRequest.type, getCashFlowStatsSaga);
}

function* watchCreateCashFlowTransaction() {
    yield takeLatest(createCashFlowTransactionRequest.type, createCashFlowTransactionSaga);
}

function* watchUpdateCashFlowTransaction() {
    yield takeLatest(updateCashFlowTransactionRequest.type, updateCashFlowTransactionSaga);
}

function* watchDeleteCashFlowTransaction() {
    yield takeLatest(deleteCashFlowTransactionRequest.type, deleteCashFlowTransactionSaga);
}

function* watchGetCommissions() {
    yield takeLatest(getCommissionsRequest.type, getCommissionsSaga);
}

function* watchGetCommissionsStats() {
    yield takeLatest(getCommissionsStatsRequest.type, getCommissionsStatsSaga);
}

function* watchUpdateCommission() {
    yield takeLatest(updateCommissionRequest.type, updateCommissionSaga);
}

function* watchSubmitIncomeForm() {
    yield takeLatest(submitIncomeFormRequest.type, submitIncomeFormSaga);
}

// ============================================================================
// ROOT SAGA
// ============================================================================

export function* erpSaga() {
    yield all([
        watchGetReceivables(),
        watchGetReceivablesStats(),
        watchCreateReceivable(),
        watchGetCashFlow(),
        watchGetCashFlowStats(),
        watchCreateCashFlowTransaction(),
        watchUpdateCashFlowTransaction(),
        watchDeleteCashFlowTransaction(),
        watchGetCommissions(),
        watchGetCommissionsStats(),
        watchUpdateCommission(),
        watchSubmitIncomeForm(),
    ]);
}
